import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from "react"
import { Plus } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

// Send glyph — Lucide `send` (matches the prototype composer button).
function SendIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="size-4">
      <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" />
      <path d="m21.854 2.147-10.94 10.939" />
    </svg>
  )
}

// Match an in-progress `@mention` token immediately before the caret: an `@`
// that starts the text or follows whitespace, then the (space-free) query typed
// so far. Returns the `@` index and the query, or null when the caret is not in
// a mention token.
function mentionContext(text, caret) {
  const upto = text.slice(0, caret)
  const match = upto.match(/(?:^|\s)@([^\s@]*)$/)
  if (!match) return null

  return { start: caret - match[1].length - 1, query: match[1] }
}

// The auto-growing message composer shared by the order Chat tab and Messages.
// Enter sends, Shift+Enter inserts a newline (design CHANGELOG Stage 5). The
// textarea grows with its content up to a cap, then scrolls. `onSend(text, {
// mentions })` runs on a non-empty submit; the parent owns the actual transport.
//
// When `conversationId` is set, typing `@` opens a participant autocomplete built
// on the DS Popover + Command primitives (AQ-012). Selecting a participant
// inserts `@DisplayName` and records the display-name→user-id map; it never
// submits the message (Enter selects while the list is open, and only sends once
// it is closed). The plain `@DisplayName` text is what the parent receives — the
// transport `.mention user-<id>` span is built downstream at the request
// boundary (PR-012).
//
// Exposes an imperative `refill({ body, mentions })` (via ref) so the
// optimistic-send flagged "Edit" affordance can drop the rejected message back
// into the box for editing. The mention name→id map is restored alongside the
// plain body so a resend re-serializes the `.mention user-<id>` span exactly like
// a fresh send (PR-012, CR-009); the box still shows plain @DisplayName.
const ChatComposer = forwardRef(function ChatComposer({
  placeholder = "Write a message…",
  onSend,
  sending = false,
  hint = "Press Enter to send · Shift + Enter for a new line",
  withAttach = false,
  conversationId = null,
  className,
}, forwardedRef) {
  const [text, setText] = useState("")
  const ref = useRef(null)

  // The recorded display-name → user-id map for the mentions currently in the
  // box. Carried alongside the plain body on send.
  const mentionsRef = useRef({})

  // Autocomplete state.
  const [people, setPeople] = useState([])
  const [menu, setMenu] = useState(null) // { start, query } while a mention is active
  const [activeIndex, setActiveIndex] = useState(0)
  const fetchedRef = useRef(false)

  const matches = menu
    ? people.filter((p) => p.name.toLowerCase().includes(menu.query.toLowerCase()))
    : []
  const menuOpen = Boolean(menu) && matches.length > 0

  const grow = () => {
    const node = ref.current
    if (!node) return
    node.style.height = "auto"
    node.style.height = `${Math.min(node.scrollHeight, 120)}px`
  }

  // Lazily load the eligible mention participants the first time the user types
  // `@`. Silent on failure — the composer still sends plain text.
  const ensurePeople = useCallback(async () => {
    if (fetchedRef.current || !conversationId) return
    fetchedRef.current = true

    try {
      const res = await fetch(`/messages/conversations/${conversationId}/mentionable`, {
        headers: { Accept: "application/json" },
      })
      if (!res.ok) return

      const data = await res.json()
      if (Array.isArray(data)) setPeople(data)
    } catch {
      // Autocomplete is a convenience; the server predicate is the authority.
    }
  }, [conversationId])

  const closeMenu = () => {
    setMenu(null)
    setActiveIndex(0)
  }

  // Recompute the mention context from the current caret position.
  const syncMenu = useCallback(() => {
    const node = ref.current
    if (!node || !conversationId) return closeMenu()

    const ctx = mentionContext(node.value, node.selectionStart ?? node.value.length)
    if (!ctx) return closeMenu()

    ensurePeople()
    setMenu(ctx)
    setActiveIndex(0)
  }, [conversationId, ensurePeople])

  useImperativeHandle(forwardedRef, () => ({
    refill(payload) {
      const { body, mentions } = payload ?? {}
      setText(body ?? "")
      // Restore the recorded display-name → user-id map so a resend re-serializes
      // the mention span (CR-009). Entries without mentions restore an empty map.
      mentionsRef.current = mentions ? { ...mentions } : {}
      const node = ref.current
      if (!node) return
      requestAnimationFrame(() => {
        node.style.height = "auto"
        node.style.height = `${Math.min(node.scrollHeight, 120)}px`
        node.focus()
        node.setSelectionRange(node.value.length, node.value.length)
      })
    },
  }), [])

  const send = () => {
    const body = text.trim()
    if (!body || sending) return

    // Only report mentions whose `@DisplayName` still survives in the body.
    const mentions = Object.fromEntries(
      Object.entries(mentionsRef.current).filter(([name]) => body.includes(`@${name}`))
    )

    onSend?.(body, { mentions })
    setText("")
    mentionsRef.current = {}
    closeMenu()
    if (ref.current) ref.current.style.height = "auto"
  }

  const applyMention = (person) => {
    const node = ref.current
    if (!node || !menu) return

    const before = text.slice(0, menu.start)
    const after = text.slice(node.selectionStart ?? text.length)
    const insert = `@${person.name} `
    const next = `${before}${insert}${after}`

    mentionsRef.current = { ...mentionsRef.current, [person.name]: person.id }
    setText(next)
    closeMenu()

    const caret = before.length + insert.length
    requestAnimationFrame(() => {
      node.focus()
      node.setSelectionRange(caret, caret)
      grow()
    })
  }

  const onKeyDown = (e) => {
    if (menuOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault()
        setActiveIndex((i) => (i + 1) % matches.length)
        return
      }
      if (e.key === "ArrowUp") {
        e.preventDefault()
        setActiveIndex((i) => (i - 1 + matches.length) % matches.length)
        return
      }
      if (e.key === "Enter" && !e.shiftKey) {
        // Enter selects from the open list — it must NOT send.
        e.preventDefault()
        applyMention(matches[activeIndex])
        return
      }
      if (e.key === "Tab") {
        e.preventDefault()
        applyMention(matches[activeIndex])
        return
      }
      if (e.key === "Escape") {
        e.preventDefault()
        closeMenu()
        return
      }
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      send()
    }
  }

  return (
    <div className={cn("shrink-0 border-t border-border px-5 py-3.5", className)}>
      <div className="flex items-end gap-[11px]">
        {withAttach && (
          <button
            type="button"
            title="Attach a file"
            className="grid size-10 shrink-0 place-items-center rounded-[10px] border border-line-2 bg-card text-ink-3 transition-colors hover:bg-secondary hover:text-muted-foreground"
          >
            <Plus className="size-[18px]" strokeWidth={1.9} />
          </button>
        )}

        <Popover open={menuOpen}>
          {/* The anchor is a wrapper div, NOT asChild on the Textarea: asChild
              prop-merging lets the anchor's data-slot="popover-anchor" clobber
              the DS Textarea's data-slot="textarea" provenance marker, which
              the e2e DS-provenance guard (and P-010 review) key on. */}
          <PopoverAnchor className="flex min-w-0 flex-1">
            <Textarea
              ref={ref}
              rows={1}
              value={text}
              onChange={(e) => {
                setText(e.target.value)
                grow()
                syncMenu()
              }}
              onKeyUp={syncMenu}
              onClick={syncMenu}
              onKeyDown={onKeyDown}
              placeholder={placeholder}
              className="max-h-[120px] min-h-0 w-full resize-none rounded-[11px] border border-line-2 bg-card px-3.5 py-[11px] text-[13px] leading-[1.4] text-foreground outline-none placeholder:text-ink-3 focus:border-ink-3 shadow-none focus-visible:border-ink-3 focus-visible:ring-0"
            />
          </PopoverAnchor>

          <PopoverContent
            align="start"
            side="top"
            sideOffset={8}
            className="w-64 p-0"
            onOpenAutoFocus={(e) => e.preventDefault()}
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            <Command shouldFilter={false} value={String(matches[activeIndex]?.id ?? "")}>
              <CommandList>
                <CommandEmpty>No people found.</CommandEmpty>
                <CommandGroup heading="Mention">
                  {matches.map((person) => (
                    <CommandItem
                      key={person.id}
                      value={String(person.id)}
                      onSelect={() => applyMention(person)}
                    >
                      {person.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <Button className="shrink-0 gap-1.5 font-bold" onClick={send} disabled={sending || !text.trim()}>
          <SendIcon />
          {sending ? "Sending…" : "Send"}
        </Button>
      </div>

      {/* The Enter-hint is desktop furniture; phones send with the button
          (S-MSG-INBOX M4 / design .th-foot .hint rule). */}
      {hint && <div className={cn("mt-[7px] text-[10.5px] text-ink-3 max-[721px]:hidden", withAttach && "pl-[51px]")}>{hint}</div>}
    </div>
  )
})

export default ChatComposer
